// createWorld(data) → World (api.ts). Wires the modules and owns every cache.
//
//   WorldData ──► actors ─► owners (borders + POV) ─► control ─► staleness
//                  │                                    │
//                  └► relations (wars, blocs, treaties, sanctions, UNGA)
//                  └► indicators ─► victory, lenses, country reports
//
// Viewpoints: a Viewer is a playable leader (real mode: that leader's state, under the leader's point
// of view) or a StateView (sandbox: any state, default worldview, no leader). `look` turns either
// into one Look that every query below reads.
//
//   'putin'          ─►  { iso3: 'RUS', pov: 'rus', leader: 'putin', key: 'putin' }
//   { iso3: 'FRA' }  ─►  { iso3: 'FRA', pov: null,  leader: null,    key: 'state:FRA' }
//
// Fog: with { fog: 'off' } the staleness model is never built and every snapshot carries one shared
// all-zero staleness array (the sandbox rebuilds the world every turn and has no parchment).
//
// Ownership of returned arrays: every typed array in a WorldSnapshot or LensResult belongs to the
// world, is shared between results (owner arrays per border epoch and POV claim set, control
// arrays per control picture) and is never written after it is returned. Callers treat them as
// read-only; copying is only needed to modify. Buffers are not recycled in place for the same
// reason: a caller may still hold an older snapshot.

import type { DateStr, Deposit, LeaderId, SourceId, War, Wonder } from '../core/schema.ts';
import { EPOCH_START, inSpan, yearOf } from '../core/dates.ts';
import { HEX_COUNT } from '../core/grid.ts';
import { f32Field, i16Field, u16Field, u8Field, type WorldData } from '../data/index.ts';
import type { LensResult, RelationInfo, VictoryEntry, VictoryTrack, Viewer, World, WorldOptions, WorldSnapshot } from './api.ts';
import { buildActors, indexOf, type ActorInfo, type ActorTable } from './actors.ts';
import { Lru } from './cache.ts';
import { worldChanges, type WorldChange } from './changes.ts';
import { activeEpisodes, buildControl, controlAt, controlHistory, controlSourcesAt, citedOrAll, frontsAt, gapsAt, type ControlHistory, type ControlModel, type SeriesGap } from './control.ts';
import { hexDetail, type HexDetailHeld, type HexFields } from './hexinfo.ts';
import { indicatorIndex } from './indicators.ts';
import { landModel } from './land.ts';
import { buildLens, lensList, type LensEnv } from './lenses.ts';
import { claimsOf, defaultOwners, ownerModel, ownersAt, type OwnerModel, type PovKey } from './owners.ts';
import { admin1Index } from './raster.ts';
import { iceArray, iceMonth, iceMonthTag, iceSources, type Hemisphere } from './seaice.ts';
import { alliesOf, buildDiplo, relContext, relationMap, relationTo, type Diplo, type RelContext } from './relations.ts';
import { foldMonths, monthOf, sampleDates, type RelationMonth } from './relhistory.ts';
import { countryReport } from './report.ts';
import { spaceLadder, type SpaceLadder } from './space.ts';
import { cityTimeline, wondersAt, withFacts } from './sites.ts';
import { buildStale, fogUnknown, ingestEvents, stalenessAt, type StaleModel } from './staleness.ts';
import { byDate, uniq } from './util.ts';
import { BASE_TRACKS, scoreEntries, victoryModel } from './victory.ts';
import { liveWars, warStatus, warViewAt, type WarView } from './wars.ts';

/** ARCHITECTURE §0: the three playable leaders and their states, used if world.json lacks perspectives. */
const LEADER_STATES: Record<LeaderId, { iso3: string; pov: PovKey }> = {
  trump: { iso3: 'USA', pov: 'usa' },
  xi: { iso3: 'CHN', pov: 'chn' },
  putin: { iso3: 'RUS', pov: 'rus' },
};

const UNGA = 'unga_ideal';

type Fog = NonNullable<WorldOptions['fog']>;

/** Whose eyes one query looks through, resolved once from a Viewer. */
interface Look {
  iso3: string;
  /** Null for a state view: the default worldview. */
  pov: PovKey | null;
  leader: LeaderId | null;
  /** Cache key: the leader id, or `state:<iso3>` (an iso3 never collides with a leader id). */
  key: string;
}

// Cache sizes: the slider revisits a handful of recent dates; each entry holds per-hex arrays.
const SNAPSHOTS_KEPT = 8;
const STALE_KEPT = 8;
const RELATIONS_KEPT = 16;
/** ≥ 27 years × 6 tracks, so a whole victorySeries stays cached. */
const VICTORY_KEPT = 200;
const LENSES_KEPT = 8;
/** Sea-ice months kept: the slider stays within a year or so of one month. */
const ICE_KEPT = 12;

/** What a snapshot carries beyond WorldSnapshot (api.ts); the view and UI feature-detect each field. */
export interface SnapshotExtras {
  /** BASIS per hex (control.ts): 0 recorded, 1 a stand-in share for a partly held unit, 2 unknown (no observation). */
  controlBasis: Uint8Array;
  /** Declared holes in a control series at the date: no front line is drawn, and the land around the last and next picture has unknown control. */
  frontGaps: SeriesGap[];
  /** Sources of the control episodes that hold `hex` for `controller` (an actor index) at the date. */
  controlSources(hex: number, controller: number): SourceId[];
}

export interface WorldImpl extends World {
  snapshot(date: DateStr, viewer: Viewer): WorldSnapshot & SnapshotExtras;
  /** Missing files and unresolved geometry noticed while loading and building. */
  readonly warnings: string[];
  /** What changed in the turn (from, to] as the viewer sees it, most important first. A state view has no claim lines. */
  changes(from: DateStr, to: DateStr, viewer: Viewer): WorldChange[];
  /** Relation of every actor index to the viewer's state at `date`: the cached map the snapshots hold. Shared, read-only. */
  relations(date: DateStr, viewer: Viewer): Map<number, RelationInfo>;
  /** Land and ice fractions per hex (0..255), for the minimap. Shared, read-only. */
  landMask(): { land: Uint8Array; ice: Uint8Array };
  /** The viewer's relation to `iso3` for each month 2000-01 → the month of `date` (default as-of, and
   * never later), with the rule that fired and its sources. A month shows the relation of highest
   * precedence that held on any sampled day of it (a war of two weeks is not lost); the final month
   * stops at `date`, so nothing after it leaks in. Shared, read-only. */
  relationHistory(iso3: string, viewer: Viewer, date?: DateStr): RelationMonth[];
  /** The space ladder of `iso3` at `date`: rungs reached (with date and sources) and the next one. */
  spaceLadder(iso3: string, date: DateStr): SpaceLadder;
  /** URL of the flag the state flew on `date` (openly licensed, public/flags/index.json), or null when it has none then. */
  flag(iso3: string, date: DateStr): string | null;
  /** Land the war's occupier holds per dated snapshot up to `date`, or null when the war has no series. */
  controlSeries(warId: string, date: DateStr): ControlHistory | null;
  /** hexInfo plus the share of the hex the controller holds (0..255). */
  hexInfo(hex: number, date: DateStr, viewer: Viewer): HexDetailHeld;
  /** The war as it stood on `date` (events, statements, intensity dated ≤ date), or null when unknown or not begun. */
  warView(warId: string, date: DateStr): WarView | null;
  /** Name, colours and kind of any actor by id (states, non-state parties, blocs), or null. */
  actorInfo(id: string): ActorInfo | null;
}

interface Grids {
  owner: Uint16Array;
  admin1: Uint16Array;
  area: Float32Array;
  land: Uint8Array;
  pop: Float32Array;
  resource: Uint8Array;
  ice: Uint8Array;
  /** Fields the hover card reads. */
  card: HexFields;
}

function grids(data: WorldData): Grids {
  const g = data.grid;
  return {
    owner: u16Field(g, 'owner'),
    admin1: u16Field(g, 'admin1'),
    area: f32Field(g, 'area_km2'),
    land: u8Field(g, 'land'),
    pop: f32Field(g, 'pop'),
    resource: u8Field(g, 'resource'),
    ice: u8Field(g, 'ice'),
    card: {
      lat: f32Field(g, 'lat'), lon: f32Field(g, 'lon'), elev: i16Field(g, 'elev'), terrain: u8Field(g, 'terrain'),
      feature: u8Field(g, 'feature'), hill: u8Field(g, 'hill'), riverSize: u8Field(g, 'river_size'),
      city: u16Field(g, 'city'), wonder: u16Field(g, 'wonder'), resource: u8Field(g, 'resource'),
    },
  };
}

/** as_of from world.json, else the newest dated fact in the baked files. */
function asOfDate(data: WorldData): DateStr {
  if (data.world.as_of) {
    return data.world.as_of;
  }
  const dates = [
    ...data.milestones.map((m) => m.date),
    ...[...data.control.values()].flatMap((s) => s.snapshots.map((x) => x.date)),
  ];
  return dates.sort(byDate).pop() ?? EPOCH_START;
}

interface Parts {
  data: WorldData;
  actors: ActorTable;
  owners: OwnerModel;
  control: ControlModel;
  /** Null with fog off: no parchment, so no facts to collect. */
  stale: StaleModel | null;
  diplo: Diplo;
  lensEnv: LensEnv;
}

function build(data: WorldData, g: Grids, fog: Fog): Parts {
  const w = data.world;
  const actors = buildActors(data.countries, w.actors, w.blocs);
  const owners = ownerModel(g.owner, data.borders, data.pov);
  const control = buildControl(w.control, {
    actors, admin1: admin1Index(g.admin1, data.admin1), areaKm2: g.area, geometry: data.geometry,
    series: data.control, warnings: data.warnings, source: (id) => data.sources.get(id),
  });
  const stale = fog === 'off' ? null : buildStale({ world: w, actors, sources: data.sources, control });
  const unga = data.indicators.series[UNGA] ?? {};
  const diplo = buildDiplo(w, actors, data.ucdp, unga, data.indicators.meta[UNGA]?.sources ?? []);
  const ix = indicatorIndex(data.indicators, data.countries.map((c) => c.iso3));
  const lensEnv: LensEnv = {
    ix, actors, diplo, pop: g.pop, area: g.area, land: g.land, resource: g.resource,
    depositSources: uniq(data.resources.deposits.flatMap((d) => d.sources)), scales: new Map(),
  };
  return { data, actors, owners, control, stale, diplo, lensEnv };
}

function leaderStates(data: WorldData): Record<LeaderId, { iso3: string; pov: PovKey }> {
  const out = { ...LEADER_STATES };
  for (const p of data.world.perspectives) {
    out[p.id] = { iso3: p.iso3, pov: p.pov };
  }
  return out;
}

/** Countries owning at least one hex (default worldview); cached per owner array. */
function presentStates(p: Parts, owner: Uint16Array, memo: WeakMap<Uint16Array, string[]>): string[] {
  const hit = memo.get(owner);
  if (hit) {
    return hit;
  }
  const seen = new Uint8Array(p.actors.maxCountry + 1);
  for (let h = 0; h < owner.length; h++) {
    seen[owner[h]] = 1;
  }
  const out = p.data.countries.filter((c) => seen[c.id] === 1).map((c) => c.iso3);
  memo.set(owner, out);
  return out;
}

/** Deposits grouped by hex, for the hover card. */
function depositsByHex(list: Deposit[]): Map<number, Deposit[]> {
  const out = new Map<number, Deposit[]>();
  for (const d of list) {
    out.set(d.hex, [...(out.get(d.hex) ?? []), d]);
  }
  return out;
}

export function createWorld(data: WorldData, opts: WorldOptions = {}): WorldImpl {
  const g = grids(data);
  const p = build(data, g, opts.fog ?? 'on');
  const deposits = depositsByHex(data.resources.deposits);
  const w = data.world;
  const leaders = leaderStates(data);
  const asOf = asOfDate(data);
  const countryByIso = new Map(data.countries.map((c) => [c.iso3, c]));
  const warById = new Map(w.wars.map((x) => [x.id, x]));
  const blocName = new Map(w.blocs.map((b) => [b.id, b.name]));
  const unitCodeOf = new Map(data.admin1.map((u) => [u.id, u.code]));
  const lakeSources = new Map((data.lakeGroups ?? []).flatMap((g) => g.hexes.map((h): [number, SourceId[]] => [h, g.sources])));
  /** Fog off: the one staleness every snapshot shares (never written). */
  const noFog = new Uint8Array(HEX_COUNT);

  const snapshots = new Lru<string, WorldSnapshot & SnapshotExtras>(SNAPSHOTS_KEPT);
  const staleCache = new Lru<string, Uint8Array>(STALE_KEPT);
  const relCache = new Lru<string, { ctx: RelContext; map: Map<number, RelationInfo> }>(RELATIONS_KEPT);
  const victoryCache = new Lru<string, VictoryEntry[]>(VICTORY_KEPT);
  const lensCache = new Lru<string, LensResult>(LENSES_KEPT);
  const iceCache = new Lru<number, Uint8Array>(ICE_KEPT);
  const monthCtx = new Map<string, RelContext[]>();
  const monthCache = new Map<string, RelationMonth[]>();
  const citiesOn = cityTimeline(withFacts(data.cities, w.cities ?? []), landModel(p.owners, data.countries));
  const wondersByYear = new Map<number, Wonder[]>();
  const present = new WeakMap<Uint16Array, string[]>();
  const ingested = new Set<number>();

  /** The viewer's eyes. A leader behaves as always; an iso3 that is no country is no state view. */
  function look(viewer: Viewer): Look {
    if (typeof viewer === 'string') {
      const v = leaders[viewer];
      return { iso3: v.iso3, pov: v.pov, leader: viewer, key: viewer };
    }
    if (!countryByIso.has(viewer.iso3)) {
      throw new Error(`world: unknown state view ${viewer.iso3}`);
    }
    return { iso3: viewer.iso3, pov: null, leader: null, key: `state:${viewer.iso3}` };
  }

  function relations(date: DateStr, self: string) {
    return relCache.use(`${date}|${self}`, () => {
      const ctx = relContext(p.diplo, self, date);
      return { ctx, map: relationMap(p.diplo, ctx) };
    });
  }

  /** Bumps whenever facts are added to the staleness model; fixed at 0 with fog off. */
  const staleVersion = (): number => p.stale?.version ?? 0;

  function staleness(date: DateStr, defOwner: Uint16Array): Uint8Array {
    const model = p.stale;
    if (model === null) {
      return noFog;
    }
    return staleCache.use(`${date}|${model.version}`, () => stalenessAt(model, date, defOwner));
  }

  function byYear<T>(memo: Map<number, T>, year: number, make: () => T): T {
    const hit = memo.get(year);
    if (hit) {
      return hit;
    }
    const v = make();
    memo.set(year, v);
    return v;
  }

  /** The date's month of sea ice over the grid, or undefined with no seaice.bin or before its first month. */
  function seaIce(date: DateStr): Uint8Array | undefined {
    const ice = data.seaIce;
    const m = ice ? iceMonth(ice, date) : null;
    if (!ice || m === null) {
      return undefined;
    }
    return iceCache.use(m, () => iceArray(ice, m));
  }

  /** Registry ids of the month's sea-ice files for a hemisphere (none without seaice.bin). */
  function iceFiles(date: DateStr, side: Hemisphere): SourceId[] {
    const ice = data.seaIce;
    const m = ice ? iceMonth(ice, date) : null;
    return ice && m !== null ? iceSources(data.sources, iceMonthTag(ice, m), side) : [];
  }

  function makeSnapshot(date: DateStr, view: Look): WorldSnapshot & SnapshotExtras {
    const defOwner = defaultOwners(p.owners, date);
    const owner = view.pov === null ? defOwner : ownersAt(p.owners, date, view.pov);
    const ctl = controlAt(p.control, date, owner, defOwner);
    const gaps = gapsAt(p.control, date);
    const stale = staleness(date, defOwner);
    const year = yearOf(date);
    return {
      date, leader: view.leader, viewer: view.iso3, owner, ...ctl,
      claim: view.pov === null ? undefined : claimsOf(p.owners, owner, defOwner),
      seaIce: seaIce(date),
      staleness: gaps.length > 0 && p.stale !== null ? fogUnknown(stale, ctl.controlBasis) : stale,
      frontGaps: gaps,
      controlSources: (hex, controller) => controlSourcesAt(p.control, date, hex, controller),
      relations: relations(date, view.iso3).map,
      activeWars: liveWars(w.wars, date),
      cities: citiesOn(date),
      wonders: byYear(wondersByYear, year, () => wondersAt(data.wonders, year)),
      deposits: data.resources.deposits,
      fronts: frontsAt(p.control, date),
    };
  }

  function snapshot(date: DateStr, viewer: Viewer): WorldSnapshot & SnapshotExtras {
    const view = look(viewer);
    return snapshots.use(`${date}|${view.key}|${staleVersion()}`, () => makeSnapshot(date, view));
  }

  const victoryOf = victoryModel({
    ix: p.lensEnv.ix, milestones: data.milestones, wonders: data.wonders,
    poles: Object.values(leaders).map((l) => l.iso3),
    allies: (iso3: string, d: DateStr) => alliesOf(p.diplo, iso3, d),
    isCountry: (id: string) => {
      const i = indexOf(p.actors, id);
      return i !== 0 && i <= p.actors.maxCountry;
    },
  });

  function victory(date: DateStr, track: VictoryTrack): VictoryEntry[] {
    if (track === 'score') {
      return victoryCache.use(`${date}|${track}`, () => scoreEntries(BASE_TRACKS.map((t) => victory(date, t))));
    }
    return victoryCache.use(`${date}|${track}`, () => {
      const states = presentStates(p, defaultOwners(p.owners, date), present);
      return victoryOf(states, date, track);
    });
  }

  function lens(id: string, date: DateStr, viewer: Viewer): LensResult {
    const view = look(viewer);
    return lensCache.use(`${id}|${date}|${view.key}|${staleVersion()}`, () => {
      const ctx = {
        date, self: view.iso3, pov: view.pov, snap: snapshot(date, viewer), defOwner: defaultOwners(p.owners, date),
        controlSources: uniq([...activeEpisodes(p.control, date).flatMap((e) => citedOrAll(p.control, e.sources, date)), ...gapsAt(p.control, date).flatMap((g) => g.sources)]),
      };
      return buildLens(id, p.lensEnv, ctx);
    });
  }

  /** Load one events year; the first load also feeds staleness. */
  async function eventYear(year: number) {
    const list = await data.loadEvents(year);
    if (!ingested.has(year)) {
      ingested.add(year);
      if (p.stale !== null) {
        ingestEvents(p.stale, p.actors, list);
      }
    }
    return list;
  }

  /** Events with from ≤ d ≤ to (both ends inclusive; a turn (D−7, D] is from = D−6). */
  async function events(from: DateStr, to: DateStr) {
    const years: number[] = [];
    for (let y = yearOf(from); y <= yearOf(to); y++) {
      years.push(y);
    }
    const lists = await Promise.all(years.map(eventYear));
    return lists.flat().filter((e) => e.d >= from && e.d <= to);
  }

  function country(iso3: string, date: DateStr, viewer: Viewer) {
    const self = look(viewer).iso3;
    const c = countryByIso.get(iso3);
    if (!c) {
      throw new Error(`world: unknown country ${iso3}`);
    }
    const rel = relations(date, self);
    const relation = relationTo(p.diplo, rel.ctx, iso3);
    const inputs = { world: w, ix: p.lensEnv.ix, diplo: p.diplo, self, blocName: (id: string) => blocName.get(id) ?? id };
    return countryReport(inputs, c, date, relation);
  }

  function hexInfo(hex: number, date: DateStr, viewer: Viewer) {
    const inputs = { fields: g.card, actors: p.actors, owners: p.owners, control: p.control, deposits, unitCode: (h: number) => unitCodeOf.get(g.admin1[h]) ?? null, lakeSources, iceSources: iceFiles };
    return hexDetail(inputs, hex, date, snapshot(date, viewer), look(viewer).pov);
  }

  /** One relContext per sampled day (relhistory.ts), per leader state: shared by every nation asked about. */
  function sampleContexts(self: string): RelContext[] {
    const hit = monthCtx.get(self);
    if (hit) {
      return hit;
    }
    const made = sampleDates(p.diplo, EPOCH_START, asOf).map((day) => relContext(p.diplo, self, day));
    monthCtx.set(self, made);
    return made;
  }

  function relationHistory(iso3: string, viewer: Viewer, date: DateStr = asOf) {
    const self = look(viewer).iso3;
    const key = `${self}|${iso3}`;
    const asSample = (ctx: RelContext) => ({ date: ctx.date, info: relationTo(p.diplo, ctx, iso3) });
    let full = monthCache.get(key);
    if (!full) {
      full = foldMonths(sampleContexts(self).map(asSample));
      monthCache.set(key, full);
    }
    if (date >= asOf) {
      return full;
    }

    // Up to `date`: whole months before it, then its own month from the days sampled up to `date`
    // and `date` itself, so nothing after it leaks in.
    const upTo = date < EPOCH_START ? EPOCH_START : date;
    const own = monthOf(upTo);
    const before = full.filter((m) => m.month < own);
    const days = sampleContexts(self).filter((ctx) => monthOf(ctx.date) === own && ctx.date < upTo);
    days.push(relContext(p.diplo, self, upTo));
    return [...before, ...foldMonths(days.map(asSample))];
  }

  function changes(from: DateStr, to: DateStr, viewer: Viewer): WorldChange[] {
    const inputs = {
      world: w, borders: data.borders, pov: data.pov, milestones: data.milestones, wonders: data.wonders,
      countries: countryByIso, actors: p.actors, control: p.control, relations: (d: DateStr, iso3: string) => relations(d, iso3).map,
    };
    return worldChanges(inputs, look(viewer), from, to);
  }

  function source(id: SourceId) {
    const s = data.sources.get(id);
    return s ? { url: s.url, title: s.title, publisher: s.publisher, accessed: s.accessed } : null;
  }

  function actorAt(index: number) {
    const a = p.actors.list[index];
    return a ? { id: a.id, name: a.name, colors: a.colors } : null;
  }

  return {
    asOf,
    countries: data.countries,
    perspectives: w.perspectives,
    warnings: data.warnings,
    actorAt,
    actorIndex: (id) => indexOf(p.actors, id),
    snapshot,
    victory,
    lens,
    lensIds: lensList,
    hexInfo,
    eventCounts: () => data.eventIndex,
    changes,
    relations: (date, viewer) => relations(date, look(viewer).iso3).map,
    landMask: () => ({ land: g.land, ice: g.ice }),
    relationHistory,
    spaceLadder: (iso3, date) => spaceLadder(data.milestones, iso3, date),
    flag: (iso3, date) => {
      const e = data.flags?.files.get(iso3);
      return e && data.flags && inSpan({ from: e.from, to: e.to ?? null }, date) ? `${data.flags.base}/${iso3}.svg` : null;
    },
    controlSeries: (warId, date) => controlHistory(p.control, warId, date, (iso3) => countryByIso.get(iso3)),
    events,
    country,
    source,
    warView: (warId, date) => {
      const war = warById.get(warId);
      return war ? warViewAt(war, w.statements, date) : null;
    },
    actorInfo: (id) => p.actors.list[indexOf(p.actors, id)] ?? null,
    warStatus: (id: string, date: DateStr) => {
      const war: War | undefined = warById.get(id);
      return war ? warStatus(war, date) : null;
    },
  };
}
